import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

// The editor is built on native contenteditable + document.execCommand — no third-party editor
// dependency (deliberate: keeps the Kotlin/JS interop surface tiny). Output is HTML, sanitized on
// render by RichContent (spec 10.1).
private fun exec(cmd: String, value: String? = null) {
    document.asDynamic().execCommand(cmd, false, value)
}

private fun tableHtml(rows: Int, cols: Int): String {
    val header = (1..cols).joinToString("") { "<th>Nadpis</th>" }
    val cells = (1..cols).joinToString("") { "<td>&nbsp;</td>" }
    val body = (1 until rows).joinToString("") { "<tr>$cells</tr>" }
    return "<table><thead><tr>$header</tr></thead><tbody>$body</tbody></table><p><br></p>"
}

/** The <td>/<th> containing the caret, or null if the caret isn't inside a table. */
private fun currentCell(): dynamic {
    val sel = window.asDynamic().getSelection()
    var node: dynamic = sel?.anchorNode ?: return null
    while (node != null) {
        val name = (node.nodeName as? String)?.lowercase()
        if (name == "td" || name == "th") return node
        node = node.parentNode
    }
    return null
}

private val BLOCK_TAGS = setOf("p", "h2", "h3", "h4", "li", "blockquote", "td", "th", "div")
private val ALIGN_CLASSES = arrayOf("align-center", "align-right", "align-justify")

/**
 * The nearest block-level ancestor of the caret, stopping at `editorEl` itself (never returns the
 * editor root -- there's nothing sensible to align there). Used for alignment, since there's no
 * `execCommand` for "set a class on the current block" the way there is for bold/italic/lists.
 */
private fun currentBlock(editorEl: HTMLElement): dynamic {
    val sel = window.asDynamic().getSelection()
    var node: dynamic = sel?.anchorNode ?: return null
    while (node != null) {
        if (node == editorEl) return null
        val name = (node.nodeName as? String)?.lowercase()
        if (name != null && name in BLOCK_TAGS) return node
        node = node.parentNode
    }
    return null
}

/**
 * Sets the current block's alignment via a `.align-*` class (not inline `style` -- RichText.kt's
 * DOMPurify allowlist has no `style` attribute, by design: content pages are admin-authored but
 * still run through the same sanitizer as everything else, and a closed set of CSS classes keeps
 * that allowlist auditable instead of opening up arbitrary inline CSS). `align = null` means left,
 * the default, so it just clears any alignment class rather than adding one.
 */
private fun setAlignment(editorEl: HTMLElement, align: String?) {
    val block = currentBlock(editorEl) ?: return
    val classList = block.classList
    ALIGN_CLASSES.forEach { classList.remove(it) }
    if (align != null) classList.add(align)
}

// Note: values obtained from dynamic DOM navigation (parentNode, querySelectorAll().item(), …) are
// already `dynamic`, so their JS methods are called directly — calling `.asDynamic()` on an
// already-dynamic value would (wrongly) emit a runtime `asDynamic()` member call and throw.

/**
 * Promotes the first row of every header-less table in `html` to a `<thead>` of `<th>` cells.
 * Pasted tables (e.g. from Excel) come as plain `<td>` rows; this gives them a real header. Tables
 * that already have a `<thead>` are left alone.
 */
private fun firstRowToHeader(html: String): String {
    val tmp = document.createElement("div")
    (tmp as HTMLElement).innerHTML = html
    val tables = tmp.asDynamic().querySelectorAll("table")
    val count = tables.length as Int
    for (i in 0 until count) {
        val table = tables.item(i)
        if (table.querySelector("thead") != null) continue
        val firstRow = table.querySelector("tr") ?: continue
        val cells = firstRow.children
        val cellCount = cells.length as Int
        if (cellCount == 0) continue
        val thead = document.createElement("thead")
        val headRow = document.createElement("tr")
        for (j in 0 until cellCount) {
            val th = document.createElement("th")
            (th as HTMLElement).innerHTML = cells.item(j).innerHTML as String
            headRow.appendChild(th)
        }
        thead.appendChild(headRow)
        firstRow.parentNode.removeChild(firstRow)
        val firstChild = table.firstChild
        if (firstChild != null) table.insertBefore(thead, firstChild) else table.appendChild(thead)
    }
    return (tmp as HTMLElement).innerHTML
}

/** Adds a row below the caret's row (matching its column count). */
private fun addTableRow() {
    val cell = currentCell() ?: return
    val row = cell.parentNode ?: return
    val cols = row.children.length as Int
    val section = row.parentNode ?: return
    val newRow = document.createElement("tr")
    repeat(cols) {
        val td = document.createElement("td")
        (td as HTMLElement).innerHTML = "&nbsp;"
        newRow.appendChild(td)
    }
    val next = row.nextSibling
    if (next != null) section.insertBefore(newRow, next) else section.appendChild(newRow)
}

/** Adds a column to the right of the caret's cell across every row of the table. */
private fun addTableColumn() {
    val cell = currentCell() ?: return
    val idx = cell.cellIndex as Int
    var table: dynamic = cell.parentNode
    while (table != null && (table.nodeName as? String)?.lowercase() != "table") table = table.parentNode
    if (table == null) return
    val rows = table.querySelectorAll("tr")
    val count = rows.length as Int
    for (i in 0 until count) {
        val r = rows.item(i)
        val isHeader = (r.parentNode?.nodeName as? String)?.lowercase() == "thead"
        val newCell = document.createElement(if (isHeader) "th" else "td")
        (newCell as HTMLElement).innerHTML = if (isHeader) "Nadpis" else "&nbsp;"
        val ref = r.children.item(idx + 1)
        if (ref != null) r.insertBefore(newCell, ref) else r.appendChild(newCell)
    }
}

@Composable
private fun ToolbarButton(label: String, title: String, onAction: () -> Unit) {
    Button(attrs = {
        classes("editor-tool")
        attr("type", "button")
        attr("title", title)
        // Keep the editor's caret/selection when clicking a toolbar button.
        onMouseDown { it.preventDefault() }
        onClick { onAction() }
    }) { Text(label) }
}

/**
 * Shared WYSIWYG editor (spec 10.1) for content pages and news. Emits the current HTML via
 * [onHtmlChange] on every edit; the initial content is set once and thereafter managed by the
 * browser (Compose never re-writes the contenteditable, so the caret doesn't jump).
 */
@Composable
fun ContentEditor(token: String?, initialHtml: String, imageBucket: String = "content-images", onHtmlChange: (String) -> Unit) {
    val scope = rememberCoroutineScope()
    var editorEl by remember { mutableStateOf<HTMLElement?>(null) }
    var uploading by remember { mutableStateOf(false) }
    var uploadError by remember { mutableStateOf<String?>(null) }
    val fileInputId = remember { "cms-img-" + kotlin.random.Random.nextInt(1_000_000) }

    fun syncHtml() {
        editorEl?.let { onHtmlChange(it.innerHTML) }
    }

    // Runs a formatting command against the editor, then pushes the new HTML up (execCommand
    // doesn't reliably fire an input event).
    fun cmd(command: String, value: String? = null) {
        exec(command, value)
        syncHtml()
    }

    Div(attrs = { classes("editor-toolbar") }) {
        ToolbarButton("H2", "Nadpis 2") { cmd("formatBlock", "h2") }
        ToolbarButton("H3", "Nadpis 3") { cmd("formatBlock", "h3") }
        ToolbarButton("¶", "Odstavec") { cmd("formatBlock", "p") }
        ToolbarButton("B", "Tučně") { cmd("bold") }
        ToolbarButton("I", "Kurzíva") { cmd("italic") }
        ToolbarButton("Vlevo", "Zarovnat vlevo") { editorEl?.let { setAlignment(it, null) }; syncHtml() }
        ToolbarButton("Na střed", "Zarovnat na střed") { editorEl?.let { setAlignment(it, "align-center") }; syncHtml() }
        ToolbarButton("Vpravo", "Zarovnat vpravo") { editorEl?.let { setAlignment(it, "align-right") }; syncHtml() }
        ToolbarButton("Do bloku", "Zarovnat do bloku") { editorEl?.let { setAlignment(it, "align-justify") }; syncHtml() }
        ToolbarButton("•", "Odrážkový seznam") { cmd("insertUnorderedList") }
        ToolbarButton("1.", "Číslovaný seznam") { cmd("insertOrderedList") }
        ToolbarButton("🔗", "Odkaz") {
            val url = window.prompt("URL odkazu:")
            if (!url.isNullOrBlank()) cmd("createLink", url)
        }
        ToolbarButton("Tabulka", "Vložit tabulku 2×2 (nebo vlož zkopírovanou z Excelu)") { cmd("insertHTML", tableHtml(2, 2)) }
        ToolbarButton("+ řádek", "Přidat řádek do tabulky") { addTableRow(); syncHtml() }
        ToolbarButton("+ sloupec", "Přidat sloupec do tabulky") { addTableColumn(); syncHtml() }
        ToolbarButton(if (uploading) "Nahrávám…" else "Obrázek", "Vložit obrázek") {
            document.getElementById(fileInputId)?.asDynamic()?.click()
        }
        Input(type = InputType.File, attrs = {
            attr("id", fileInputId)
            attr("accept", "image/*")
            style { display(DisplayStyle.None) }
            onChange { e ->
                val input = e.target as HTMLInputElement
                val file = input.files?.item(0) ?: return@onChange
                uploading = true
                uploadError = null
                scope.launch {
                    try {
                        val safe = file.name.replace(Regex("[^A-Za-z0-9._-]"), "_")
                        val path = "${kotlin.random.Random.nextInt(1_000_000)}-$safe"
                        val url = Api.uploadContentImage(token, path, file, imageBucket)
                        editorEl?.focus()
                        cmd("insertHTML", "<img src=\"$url\" alt=\"\">")
                    } catch (ex: Throwable) {
                        uploadError = "Nahrání obrázku selhalo: ${ex.apiMessage()}"
                    } finally {
                        uploading = false
                        input.value = ""
                    }
                }
            }
        })
    }

    Div(attrs = {
        classes("editor-area")
        attr("contenteditable", "true")
    }) {
        DisposableRefEffect(Unit) { el ->
            val html = el as HTMLElement
            html.innerHTML = initialHtml
            editorEl = html
            val inputListener: (org.w3c.dom.events.Event) -> Unit = { syncHtml() }
            // Sanitize pasted HTML (e.g. a table copied from Excel carries lots of mso-* cruft the
            // allowlist strips) and insert the clean version. Plain-text pastes use the default path.
            val pasteListener: (org.w3c.dom.events.Event) -> Unit = { ev ->
                val clipboard = ev.asDynamic().clipboardData
                val pastedHtml = clipboard?.getData("text/html") as? String
                if (!pastedHtml.isNullOrBlank()) {
                    ev.preventDefault()
                    exec("insertHTML", firstRowToHeader(sanitizeHtml(pastedHtml)))
                    syncHtml()
                }
            }
            html.addEventListener("input", inputListener)
            html.addEventListener("paste", pasteListener)
            onDispose {
                html.removeEventListener("input", inputListener)
                html.removeEventListener("paste", pasteListener)
                editorEl = null
            }
        }
    }

    uploadError?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
}
